import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { TID } from '@/constants/testIds';
import { api } from '@/lib/api';
import { ChevronRight, UserPlus, Eye, EyeOff } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const LOGO = '/logo.png';

export default function Register() {
  const { register, loginWithGoogle, error } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Coaches share the link as either `?invite=...` or `?token=...`
  const inviteToken = searchParams.get('invite') || searchParams.get('token');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(inviteToken ? 'player' : 'coach');
  // Coach onboarding: ask for the first team's name during signup so the
  // app no longer auto-creates "Mi Equipo" for everyone.
  const [teamName, setTeamName] = useState('');
  const [teamCategory, setTeamCategory] = useState('mixed');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const isInviteFlow = !!inviteToken;

  useEffect(() => {
    if (!inviteToken) return;
    // Verify the invitation. Click tracking is fired by /r/:slug (InviteRedirect)
    // so we don't track here to avoid double-counting.
    api.get(`/invitations/verify/${inviteToken}`)
      .then(r => setInviteInfo(r.data))
      .catch(() => setErr('Invitación inválida o expirada'));
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      // Always pass the canonical token (so slug-based links still work server-side)
      const canonical = inviteInfo?.token || inviteToken;
      // For invite-based player registration we only ask for name + password.
      // Auto-generate a unique email so the user model stays valid; we display it
      // to the player after register so they can use it for future logins.
      let finalEmail = email;
      if (isInviteFlow) {
        const slug = (name || 'jugador')
          .toLowerCase()
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
          .slice(0, 24) || 'jugador';
        const rand = Math.random().toString(36).slice(2, 8);
        finalEmail = `${slug}-${rand}@p.playmasteria.team`;
        setGeneratedEmail(finalEmail);
      }
      const u = await register({
        name,
        email: finalEmail,
        password,
        role,
        invite_token: canonical || undefined,
        // Only sent for the coach flow — backend ignores otherwise.
        team_name: role === 'coach' ? (teamName || '').trim() : undefined,
        team_category: role === 'coach' ? teamCategory : undefined,
      });
      // For invite flow, show the generated credentials briefly before navigating.
      if (isInviteFlow) {
        setTimeout(() => navigate(u.role === 'player' ? '/playbook' : '/dashboard'), 4500);
      } else {
        navigate(u.role === 'player' ? '/playbook' : '/dashboard');
      }
    } catch (e) {
      setErr(error || 'Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async (resp) => {
    setErr(null);
    try {
      const u = await loginWithGoogle(resp.credential, role);
      navigate(u.role === 'player' ? '/playbook' : '/dashboard');
    } catch {
      setErr('No se pudo registrarse con Google');
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-4 py-8">
      <div className="absolute inset-0 court-grad pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6">
          <img src={LOGO} alt="PlayMaster IA" className="h-24" />
        </Link>

        <div className="bg-card-steel border-2 border-gold/40 rounded-lg p-8 shadow-2xl">
          <h1 className="font-h uppercase text-3xl mb-1">{t('create_account')}</h1>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-6">
            {t('register')}
          </div>

          {inviteInfo && (
            <div className="mb-5 bg-gold/10 border border-gold/40 rounded-md p-3 flex items-center gap-3">
              {inviteInfo.team_logo_url ? (
                <img
                  src={inviteInfo.team_logo_url}
                  alt=""
                  className="w-12 h-12 rounded-md object-cover border border-gold/40"
                  data-testid="register-invite-logo"
                />
              ) : (
                <UserPlus className="w-4 h-4 text-gold" />
              )}
              <div className="text-sm">
                {inviteInfo.team_name
                  ? t('register_invite_coach_team', {
                      coach: inviteInfo.coach_name,
                      team: inviteInfo.team_name,
                    })
                  : t('register_invite_coach_no_team', { coach: inviteInfo.coach_name })}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="flex flex-col gap-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">{t('name')}</label>
              <input
                data-testid={TID.registerName}
                required
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 text-white outline-none transition"
              />
            </div>
            {!isInviteFlow && (
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">{t('email')}</label>
                <input
                  data-testid={TID.registerEmail}
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 text-white outline-none transition"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">{t('password')}</label>
              <div className="relative">
                <input
                  data-testid={TID.registerPassword}
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 pr-12 text-white outline-none transition"
                />
                <button
                  type="button"
                  data-testid="register-password-toggle"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? t('register_hide_password') : t('register_show_password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gold/70 hover:text-gold"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {!inviteToken && (
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">{t('role')}</label>
                <select
                  data-testid={TID.registerRole}
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 text-white outline-none transition"
                >
                  <option value="coach">{t('coach')}</option>
                  <option value="player">{t('player')}</option>
                </select>
              </div>
            )}
            {/* Coach onboarding: ask for the first team's name + category so
                the app stops auto-creating "Mi Equipo". This becomes the
                coach's active default team on first login. */}
            {!inviteToken && role === 'coach' && (
              <>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">
                    {t('register_team_name') || 'Nombre de tu equipo principal'}
                  </label>
                  <input
                    value={teamName}
                    onChange={e => setTeamName(e.target.value)}
                    maxLength={80}
                    required
                    placeholder="Ej. Lobos 15U"
                    data-testid="register-team-name"
                    className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 text-white outline-none transition"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    {t('register_team_name_help') || 'Es el primer equipo que vas a ver al entrar. Después podés crear más desde Equipos.'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-widest text-gold mb-1 block">
                    {t('register_team_category') || 'Categoría'}
                  </label>
                  <select
                    value={teamCategory}
                    onChange={e => setTeamCategory(e.target.value)}
                    data-testid="register-team-category"
                    className="w-full bg-navy-deep border border-steel focus:border-gold rounded-md px-4 py-3 text-white outline-none transition"
                  >
                    <option value="mixed">{t('roster_cat_mixed') || 'Mixto'}</option>
                    <option value="male">{t('roster_cat_male') || 'Masculino'}</option>
                    <option value="female">{t('roster_cat_female') || 'Femenino'}</option>
                  </select>
                </div>
              </>
            )}
            {err && <div className="text-alert text-sm bg-alert/10 border border-alert/40 rounded p-2">{err}</div>}
            {generatedEmail && (
              <div className="text-xs bg-gold/10 border border-gold/40 rounded p-3 leading-relaxed" data-testid="register-generated-email">
                <div className="font-h uppercase tracking-widest text-gold mb-1">{t('register_your_username')}</div>
                <div className="font-mono text-white break-all">{generatedEmail}</div>
                <div className="text-gray-400 mt-2">{t('register_save_credentials')}</div>
              </div>
            )}
            <button
              data-testid={TID.registerSubmit}
              disabled={loading}
              type="submit"
              className="mt-2 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-gold text-navy hover:bg-gold-light font-h uppercase tracking-wider font-semibold disabled:opacity-50 transition"
            >
              {loading ? '...' : t('register')} <ChevronRight className="w-4 h-4" />
            </button>
          </form>

          {!isInviteFlow && (
            <>
              <div className="my-4 flex items-center gap-3" data-testid="register-google-divider">
                <div className="flex-1 h-px bg-steel" />
                <span className="text-[10px] text-gray-500 uppercase tracking-widest">o</span>
                <div className="flex-1 h-px bg-steel" />
              </div>

              <div className="flex justify-center" data-testid="register-google-btn">
                <GoogleLogin
                  onSuccess={handleGoogle}
                  onError={() => setErr('No se pudo iniciar sesión con Google')}
                  theme="filled_black"
                  shape="rectangular"
                  text="signup_with"
                  locale={t('register_google_locale')}
                  width="320"
                />
              </div>
            </>
          )}

          <div className="mt-6 text-center text-sm text-gray-400">
            <Link to="/login" className="text-gold hover:underline">{t('login')}</Link>
          </div>

          <p className="mt-4 text-center text-[11px] text-gray-500 leading-relaxed" data-testid="register-legal-links">
            {t('register_terms_intro')}{' '}
            <Link to="/terms" className="text-gold hover:underline">{t('register_terms_link')}</Link>
            {' '}{t('register_terms_and')}{' '}
            <Link to="/privacy" className="text-gold hover:underline">{t('register_privacy_link')}</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
